import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SettingsSection, SettingsValue } from '@hcd/domain'
import { toast } from 'sonner'
import { api, ApiError } from './api'

// ---- settings sections -------------------------------------------------
interface SectionState<K extends SettingsSection> {
  value: SettingsValue<K>
  version: number
  updatedAt: string | null
}
type AllSettings = { [K in SettingsSection]: SectionState<K> }

export const settingsQuery = queryOptions({
  queryKey: ['settings'],
  queryFn: () =>
    api.get<{ sections: AllSettings; server: { emailSending: 'suppressed' | 'live' } }>(
      '/settings',
    ),
})

export function useSaveSection<K extends SettingsSection>(section: K) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { value: SettingsValue<K>; expectedVersion: number }) =>
      api.put<SectionState<K>>(`/settings/${section}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] })
      toast.success('Settings saved')
    },
    onError: (err) => reportError(err, () => void qc.invalidateQueries({ queryKey: ['settings'] })),
  })
}

// ---- catalogue resources ------------------------------------------------
export interface Meta {
  id: string
  active: boolean
  version: number
  updatedAt: string
}
export interface VatRate extends Meta {
  name: string
  code: string
  rateBp: number
  isDefault: boolean
  sort: number
}
export interface NamedItem extends Meta {
  name: string
  sort: number
}
export interface PriceItem extends Meta {
  code: string
  name: string
  description: string
  internalNotes: string
  categoryId: string | null
  unitId: string
  chargeType: 'one_off' | 'recurring'
  frequency: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'annually' | null
  sellPence: number
  costPence: number
  minimumPence: number
  vatRateId: string
  sort: number
}
export interface ContractType extends Meta {
  name: string
  description: string
  requiresCountersignature: boolean
  sort: number
}
export interface OnboardingItem extends Meta {
  name: string
  kind: 'sign' | 'form' | 'acknowledge' | 'upload'
  description: string
  required: boolean
  sort: number
  /** A library document the starter opens to read or sign (e.g. the handbook). */
  documentId: string | null
  form: 'starter_details' | 'uniform' | null
}

export const resourceQuery = <T>(path: string) =>
  queryOptions({
    queryKey: ['resource', path],
    queryFn: async () => (await api.get<{ items: T[] }>(`/${path}?includeArchived=true`)).items,
  })

export function useResource<T extends Meta>(path: string) {
  return useQuery(resourceQuery<T>(path))
}

export function useResourceMutations<T extends Meta>(path: string, noun: string) {
  const qc = useQueryClient()
  const refresh = () => void qc.invalidateQueries({ queryKey: ['resource', path] })
  const create = useMutation({
    mutationFn: (body: Partial<T>) => api.post<T>(`/${path}`, body),
    onSuccess: () => {
      refresh()
      toast.success(`${capital(noun)} added`)
    },
    onError: (e) => reportError(e, refresh),
  })
  const update = useMutation({
    mutationFn: ({ id, ...body }: Partial<T> & { id: string; expectedVersion: number }) =>
      api.patch<T>(`/${path}/${id}`, body),
    onSuccess: (saved, vars) => {
      refresh()
      const archived = 'active' in vars && vars.active === false
      const restored = 'active' in vars && vars.active === true
      toast.success(
        archived
          ? `${capital(noun)} archived`
          : restored
            ? `${capital(noun)} restored`
            : `${capital(noun)} saved`,
      )
      return saved
    },
    onError: (e) => reportError(e, refresh),
  })
  return { create, update }
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export function reportError(err: unknown, reload?: () => void) {
  if (
    err instanceof ApiError &&
    (err.code === 'version_conflict' || err.status === 409) &&
    reload &&
    err.code === 'version_conflict'
  ) {
    toast.error(err.message, { action: { label: 'Reload', onClick: reload }, duration: 10_000 })
    return
  }
  toast.error(err instanceof Error ? err.message : 'Something went wrong.')
}
